// @flow
/**
 * Normalizes search/channel API responses to the format expected by the app.
 * Groups results by ugc_id to merge syndicated products and builds associated_products array.
 */
export function normalizeSearchResponse(response: any) {
  if (!response.ugc_results) {
    return response;
  }

  if (response.ugc_results.length === 0) {
    return response.ugc_results;
  }

  const pwr_observations = response.pwr_observation_codes;

  // Group results by ugc_id to merge syndicated products
  const grouped = {};
  response.ugc_results.forEach(ugc => {
    const key = ugc.ugc_id;
    if (!grouped[key]) {
      grouped[key] = { primary: ugc, rows: [], associated_products: [] };
    }
    grouped[key].rows.push(ugc);
    grouped[key].associated_products.push({
      page_id: ugc.page_id,
      product_name: ugc.product_name,
      product_image_url: ugc.product_image_url,
    });
  });

  return Object.values(grouped)
    .sort(
      (a, b) =>
        new Date(b.primary.created_date) - new Date(a.primary.created_date)
    )
    .map(({ primary: ugc, rows, associated_products }) => {
      const {
        answer,
        assigned_by,
        assigned_to,
        assignment_created_date,
        assignment_updated_date,
        author,
        brand,
        caption,
        category_name,
        catalog_id,
        catalog_name,
        client_id,
        client_name,
        client_specific_questions,
        comments,
        created_date,
        custom_observation_codes,
        device_id,
        editable,
        email,
        expert,
        featured,
        headline,
        ip_address,
        locale,
        location,
        new_ugc_id,
        observations,
        old_ugc_id,
        page_id,
        primary_contents,
        product_description,
        product_image_url,
        product_name,
        product_url,
        publication_status,
        question,
        question_type,
        rating,
        service_comments,
        bottom_line,
        source,
        subject_id,
        ugc_edit_request_date,
        ugc_id,
        ugc_related_content,
        ugc_type,
        url,
        variant_id,
      } = ugc;

      // Pull related content from any row in the group that has it
      // (API may return it on the primary row, on variant rows, or both).
      const relatedSource =
        ugc_related_content ||
        (rows.find(r => r.ugc_related_content) || {}).ugc_related_content ||
        null;
      // Backend ships `ugc_type`; downstream models read `ugctype`.
      const normalizedRelated = relatedSource
        ? relatedSource.map(rc => ({
            ...rc,
            ugctype: rc.ugc_type,
          }))
        : null;

      return {
        subject: {
          associated_products:
            associated_products.length > 1 ? associated_products : [],
          brand_name: brand,
          category_name,
          description: product_description,
          merchant: {
            id: catalog_id,
            name: catalog_name,
            group_id: client_id,
            group_name: client_name,
          },
          page_id,
          page_id_variant: variant_id,
          product_image_url,
          product_url,
          subject_id,
          subject_type: 'Product',
          name: product_name || 'Unknown Product',
        },
        ugc: {
          answer,
          assignment_created_date,
          assignment_updated_date,
          assigned_to,
          assigned_by,
          author,
          caption,
          client_specific_question: client_specific_questions,
          comments,
          created_date,
          device_id,
          editable,
          email,
          expert,
          featured,
          ip_address,
          locale,
          location,
          new_ugc_id,
          old_ugc_id,
          published: publication_status === 'APPROVED' ? true : false,
          question,
          question_type,
          rating,
          review_comments: ugc_type === 'Review' ? primary_contents : null,
          review_headline: headline,
          service_comments,
          bottom_line,
          source,
          subject_id,
          ugc_edit_request_date,
          ugc_id,
          ugc_related_content: normalizedRelated,
          ugctype: ugc_type,
          url,
        },
        custom_observations: custom_observation_codes,
        existing_observations: observations,
        pwr_observations: pwr_observations,
      };
    });
}



// WEBPACK FOOTER //
// ./src/containers/search/normalizeSearchResponse.js